import { useState, useRef, useEffect } from 'react';
import { Plus, Trash2, Check, CircleUser, Search, X } from 'lucide-react';
import type { SubTask, Task } from '../../types';
import Avatar from '../common/Avatar';
import { useApp } from '../../context/AppContext';

interface SubtaskListProps {
  task: Task;
  onUpdate: (subtasks: SubTask[]) => void;
}

interface AssigneeComboboxProps {
  assigneeId: string | null;
  onSelect: (userId: string | null) => void;
}

function AssigneeCombobox({ assigneeId, onSelect }: AssigneeComboboxProps) {
  const { state } = useApp();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const assignee = assigneeId
    ? state.users.find((u) => u.id === assigneeId)
    : null;

  const filtered = state.users.filter((u) =>
    u.name.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setSearch('');
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
          setSearch('');
        }}
        className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors text-xs min-w-[110px]"
        title={assignee ? assignee.name : 'Sem recurso'}
      >
        {assignee ? (
          <>
            <Avatar
              name={assignee.name}
              color={assignee.avatarColor}
              size="sm"
            />
            <span className="truncate max-w-[70px]">{assignee.name}</span>
          </>
        ) : (
          <>
            <CircleUser size={16} className="text-slate-400 shrink-0" />
            <span className="text-slate-400">Sem recurso</span>
          </>
        )}
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 w-60 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
              <Search size={14} className="text-slate-400 shrink-0" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar usuário..."
                className="bg-transparent outline-none text-xs text-slate-700 placeholder:text-slate-400 w-full"
                autoFocus
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto py-1">
            {assignee && (
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                  setSearch('');
                }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors"
              >
                <X size={14} />
                <span>Remover</span>
              </button>
            )}
            {filtered.map((user) => (
              <button
                key={user.id}
                type="button"
                onClick={() => {
                  onSelect(user.id);
                  setOpen(false);
                  setSearch('');
                }}
                className={`flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-slate-50 transition-colors ${user.id === assigneeId
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-700'
                  }`}
              >
                <Avatar
                  name={user.name}
                  color={user.avatarColor}
                  size="sm"
                />
                <span className="truncate">{user.name}</span>
                {user.id === assigneeId && (
                  <Check size={14} className="ml-auto text-brand-600 shrink-0" />
                )}
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-400 text-center">
                Nenhum usuário encontrado
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SubtaskList({ task, onUpdate }: SubtaskListProps) {
  const { apiAddSubtask, apiUpdateSubtask, apiDeleteSubtask } = useApp();
  const subtasks = task.subtasks;
  const completedCount = subtasks.filter((s) => s.completed).length;
  const totalCount = subtasks.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const today = new Date().toISOString().split('T')[0];

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingId]);

  async function handleAdd() {
    const created:any = await apiAddSubtask(task.id, {
      title: '',
      assigneeId: null,
      hours: 0,
      startDate: null,
      endDate: null,
      completed: false,
    });
    if (created) {
      onUpdate([...subtasks, created]);
      setEditingId(created.id);
      setEditingTitle('');
    }
  }

  async function handleToggleComplete(id: string) {
    const subtask = subtasks.find((s) => s.id === id);
    if (!subtask) return;
    const newCompleted = !subtask.completed;
    await apiUpdateSubtask({ id, completed: newCompleted });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, completed: newCompleted } : s));
  }

  async function handleDelete(id: string) {
    await apiDeleteSubtask(id);
    onUpdate(subtasks.filter((s) => s.id !== id));
  }

  function handleTitleClick(subtask: SubTask) {
    setEditingId(subtask.id);
    setEditingTitle(subtask.title);
  }

  async function handleTitleSave(id: string) {
    await apiUpdateSubtask({ id, title: editingTitle });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, title: editingTitle } : s));
    setEditingId(null);
    setEditingTitle('');
  }

  function handleTitleKeyDown(e: React.KeyboardEvent, id: string) {
    if (e.key === 'Enter') {
      handleTitleSave(id);
    } else if (e.key === 'Escape') {
      setEditingId(null);
      setEditingTitle('');
    }
  }

  async function handleAssigneeChange(id: string, userId: string | null) {
    await apiUpdateSubtask({ id, assigneeId: userId });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, assigneeId: userId } : s));
  }

  async function handleHoursChange(id: string, hours: number) {
    await apiUpdateSubtask({ id, hours });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, hours } : s));
  }

  async function handleStartDateChange(id: string, startDate: string | null) {
    await apiUpdateSubtask({ id, startDate });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, startDate } : s));
  }

  async function handleEndDateChange(id: string, endDate: string | null) {
    await apiUpdateSubtask({ id, endDate });
    onUpdate(subtasks.map((s) => s.id === id ? { ...s, endDate } : s));
  }

  function isOverdue(subtask: SubTask): boolean {
    return (
      !!subtask.endDate && subtask.endDate < today && !subtask.completed
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-slate-800">Subtarefas</h4>
          {totalCount > 0 && (
            <span className="text-xs text-slate-500">
              {completedCount} de {totalCount} concluídas
            </span>
          )}
          {totalCount > 0 && (
            <div className="flex-1 max-w-[200px]">
              <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-500 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleAdd}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
        >
          <Plus size={14} />
          Adicionar subtarefa
        </button>
      </div>
      {subtasks.length > 0 && (
        <div className="space-y-1">
          {subtasks.map((subtask) => {
            const overdue = isOverdue(subtask);
            return (
              <div
                key={subtask.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${overdue
                    ? 'bg-red-50 border-red-200'
                    : 'bg-white border-slate-100 hover:border-slate-200'
                  }`}
              >
                <button
                  type="button"
                  onClick={() => handleToggleComplete(subtask.id)}
                  className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${subtask.completed
                      ? 'bg-brand-500 border-brand-500 text-white'
                      : 'border-slate-300 hover:border-brand-400'
                    }`}
                >
                  {subtask.completed && <Check size={10} strokeWidth={3} />}
                </button>

                <div className="flex-1 min-w-0">
                  {editingId === subtask.id ? (
                    <input
                      ref={editInputRef}
                      type="text"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onBlur={() => handleTitleSave(subtask.id)}
                      onKeyDown={(e) => handleTitleKeyDown(e, subtask.id)}
                      placeholder="Título da subtarefa..."
                      className="w-full text-sm bg-transparent outline-none border-b border-brand-300 py-0.5 text-slate-800 placeholder:text-slate-400"
                    />
                  ) : (
                    <span
                      onClick={() => handleTitleClick(subtask)}
                      className={`text-sm cursor-pointer truncate block ${subtask.completed
                          ? 'line-through text-slate-400'
                          : 'text-slate-700'
                        }`}
                    >
                      {subtask.title || (
                        <span className="text-slate-400 italic">
                          Sem título
                        </span>
                      )}
                    </span>
                  )}
                </div>

                <AssigneeCombobox
                  assigneeId={subtask.assigneeId}
                  onSelect={(userId) =>
                    handleAssigneeChange(subtask.id, userId)
                  }
                />

                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    value={subtask.hours}
                    onChange={(e) =>
                      handleHoursChange(
                        subtask.id,
                        parseFloat(e.target.value) || 0
                      )
                    }
                    className="w-14 text-xs text-center border border-slate-200 rounded-lg px-1.5 py-1 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                  <span className="text-xs text-slate-400">h</span>
                </div>

                <input
                  type="date"
                  value={subtask.startDate || ''}
                  onChange={(e) =>
                    handleStartDateChange(
                      subtask.id,
                      e.target.value || null
                    )
                  }
                  title="Data de início"
                  className="text-xs border border-slate-200 rounded-lg px-1.5 py-1 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 text-slate-600 w-[120px]"
                />

                <input
                  type="date"
                  value={subtask.endDate || ''}
                  onChange={(e) =>
                    handleEndDateChange(
                      subtask.id,
                      e.target.value || null
                    )
                  }
                  title="Data de término"
                  className={`text-xs border rounded-lg px-1.5 py-1 outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 w-[120px] ${overdue
                      ? 'border-red-300 text-red-600'
                      : 'border-slate-200 text-slate-600'
                    }`}
                />

                <button
                  type="button"
                  onClick={() => handleDelete(subtask.id)}
                  className="p-1 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
                  title="Excluir subtarefa"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {subtasks.length === 0 && (
        <div className="text-center py-6 text-sm text-slate-400">
          Nenhuma subtarefa adicionada
        </div>
      )}
    </div>
  );
}
